import React from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Accordion,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMobileAlt,
  faHeart,
  faImages,
  faFaceSmile,
  faUpload,
  faComments,
  faLock,
  faDownload,
  faUsers,
  faBolt,
  faArrowRight,
  faFaceFrown,
  faWind,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import HeaderLanding from "../components/HeaderLanding";
import SlideIn from "../components/SlideIn";

// ─── Hero ────────────────────────────────────────────────────────────────────
function HeroSection() {
  const { t } = useTranslation();
  return (
    <section
      className="py-5 text-white text-center"
      style={{
        background: "linear-gradient(135deg, #6a11cb 0%, #c850c0 50%, #f857a6 100%)",
        padding: "100px 20px 90px",
      }}
    >
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span
            className="badge rounded-pill mb-4 d-inline-block px-3 py-2"
            style={{ backgroundColor: "rgba(255,255,255,0.2)", fontSize: "0.8rem" }}
          >
            {t("user_landing.hero.tag")}
          </span>
          <h1
            className="display-4 fw-bold mb-4"
            style={{ maxWidth: 720, margin: "0 auto 1.5rem" }}
          >
            {t("user_landing.hero.title")}
          </h1>
          <p
            className="lead mb-5"
            style={{
              maxWidth: 580,
              margin: "0 auto 2rem",
              fontSize: "1.2rem",
              opacity: 0.9,
            }}
          >
            {t("user_landing.hero.subtitle")}
          </p>
          <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
            <Button
              as={Link}
              to="/login"
              size="lg"
              style={{
                backgroundColor: "white",
                color: "#6a11cb",
                fontWeight: 700,
                border: "none",
              }}
              className="px-5"
            >
              {t("user_landing.hero.cta")}
              <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </Button>
          </div>
        </motion.div>
      </Container>
    </section>
  );
}

// ─── Problem ──────────────────────────────────────────────────────────────────
function ProblemSection() {
  const { t } = useTranslation();
  const problems = [
    { key: "p1", icon: faFaceFrown },
    { key: "p2", icon: faMobileAlt },
    { key: "p3", icon: faWind },
  ];
  return (
    <section className="py-5" style={{ backgroundColor: "#fff9f9" }}>
      <Container>
        <SlideIn>
          <h2 className="text-center fw-bold mb-5">{t("user_landing.problem.title")}</h2>
          <Row className="g-4 justify-content-center">
            {problems.map(({ key, icon }) => (
              <Col md={4} key={key}>
                <Card className="h-100 border-0 shadow-sm rounded-4 p-4 text-center">
                  <div style={{ fontSize: "2.5rem", marginBottom: "1rem", color: "#888" }}>
                    <FontAwesomeIcon icon={icon} />
                  </div>
                  <p className="fw-semibold fs-5 mb-0">{t(`user_landing.problem.${key}_text`)}</p>
                </Card>
              </Col>
            ))}
          </Row>
        </SlideIn>
      </Container>
    </section>
  );
}

// ─── Feature Highlights ───────────────────────────────────────────────────────
function FeatureHighlights() {
  const { t } = useTranslation();
  return (
    <section className="py-5" style={{ backgroundColor: "#ffffff" }}>
      <Container>
        <SlideIn>
          <h2 className="text-center fw-bold mb-2">{t("user_landing.solution.title")}</h2>
          <p className="text-center text-secondary mb-5 fs-5">
            {t("user_landing.solution.subtitle")}
          </p>
        </SlideIn>

        {/* AI Face Search */}
        <SlideIn>
          <Row className="align-items-center g-5 mb-5">
            <Col md={6}>
              <div
                className="d-flex align-items-center justify-content-center rounded-4"
                style={{
                  height: 260,
                  background: "linear-gradient(135deg, #f3e7ff 0%, #ffe4f3 100%)",
                }}
              >
                <div className="text-center p-4">
                  <div
                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: 96, height: 96, backgroundColor: "#6a11cb" }}
                  >
                    <FontAwesomeIcon icon={faFaceSmile} size="2x" className="text-white" />
                  </div>
                  <div className="fw-bold fs-5" style={{ color: "#6a11cb" }}>
                    {t("user_landing.solution.face_stat")}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div
                style={{
                  width: 40,
                  height: 4,
                  backgroundColor: "#6a11cb",
                  borderRadius: 2,
                  marginBottom: "1rem",
                }}
              />
              <h3 className="fw-bold mb-3">{t("user_landing.solution.face_title")}</h3>
              <p className="text-secondary fs-5 mb-0">{t("user_landing.solution.face_desc")}</p>
            </Col>
          </Row>
        </SlideIn>

        {/* Event Wall */}
        <SlideIn>
          <Row className="align-items-center g-5 flex-md-row-reverse">
            <Col md={6}>
              <div
                className="d-flex align-items-center justify-content-center rounded-4"
                style={{
                  height: 260,
                  background: "linear-gradient(135deg, #ffe4f3 0%, #f3e7ff 100%)",
                }}
              >
                <div className="text-center p-4">
                  <div
                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: 96, height: 96, backgroundColor: "#c850c0" }}
                  >
                    <FontAwesomeIcon icon={faUsers} size="2x" className="text-white" />
                  </div>
                  <div className="fw-bold fs-5" style={{ color: "#c850c0" }}>
                    {t("user_landing.solution.guest_stat")}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div
                style={{
                  width: 40,
                  height: 4,
                  backgroundColor: "#c850c0",
                  borderRadius: 2,
                  marginBottom: "1rem",
                }}
              />
              <h3 className="fw-bold mb-3">{t("user_landing.solution.guest_title")}</h3>
              <p className="text-secondary fs-5 mb-0">{t("user_landing.solution.guest_desc")}</p>
            </Col>
          </Row>
        </SlideIn>
      </Container>
    </section>
  );
}

// ─── How It Works ─────────────────────────────────────────────────────────────
function HowItWorksSection() {
  const { t } = useTranslation();
  const steps = [
    { key: "step1", icon: faImages, color: "#6a11cb" },
    { key: "step2", icon: faFaceSmile, color: "#c850c0" },
    { key: "step3", icon: faDownload, color: "#f857a6" },
    { key: "step4", icon: faHeart, color: "#e63946" },
  ];
  return (
    <section className="py-5" style={{ backgroundColor: "#faf5ff" }}>
      <Container>
        <SlideIn>
          <h2 className="text-center fw-bold mb-2">{t("user_landing.how_it_works.title")}</h2>
          <p className="text-center text-secondary mb-5">
            {t("user_landing.how_it_works.subtitle")}
          </p>
          <Row className="g-4">
            {steps.map(({ key, icon, color }) => (
              <Col xs={6} md={3} key={key}>
                <div className="text-center p-3">
                  <div
                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: 64, height: 64, backgroundColor: color }}
                  >
                    <FontAwesomeIcon icon={icon} size="lg" className="text-white" />
                  </div>
                  <div
                    className="fw-bold mb-1 d-inline-block px-2 py-1 rounded-pill"
                    style={{
                      backgroundColor: color,
                      color: "white",
                      fontSize: "0.7rem",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {t(`user_landing.how_it_works.${key}_num`)}
                  </div>
                  <h5 className="fw-bold mt-2 mb-1">
                    {t(`user_landing.how_it_works.${key}_title`)}
                  </h5>
                  <p className="text-secondary small mb-0">
                    {t(`user_landing.how_it_works.${key}_desc`)}
                  </p>
                </div>
              </Col>
            ))}
          </Row>
        </SlideIn>
      </Container>
    </section>
  );
}

// ─── Event Wall Spotlight ──────────────────────────────────────────────────
function EventWallSpotlight() {
  const { t } = useTranslation();
  const tags = ["tag1", "tag2", "tag3", "tag4"];
  const tagIcons = [faUpload, faHeart, faUsers, faImages];
  const stats = [
    {
      icon: faUpload,
      titleKey: "user_landing.guest_gallery.stat1_title",
      descKey: "user_landing.guest_gallery.stat1_desc",
    },
    {
      icon: faHeart,
      titleKey: "user_landing.guest_gallery.stat2_title",
      descKey: "user_landing.guest_gallery.stat2_desc",
    },
    {
      icon: faComments,
      titleKey: "user_landing.guest_gallery.stat3_title",
      descKey: "user_landing.guest_gallery.stat3_desc",
    },
  ];
  return (
    <section
      className="py-5"
      style={{
        background: "linear-gradient(135deg, #6a11cb 0%, #c850c0 100%)",
        color: "white",
      }}
    >
      <Container>
        <SlideIn>
          <Row className="align-items-center g-5">
            <Col md={6} className="d-flex flex-column gap-3">
              {stats.map(({ icon, titleKey, descKey }) => (
                <div
                  key={titleKey}
                  className="d-flex align-items-start gap-3 p-3 rounded-3"
                  style={{ backgroundColor: "rgba(255,255,255,0.12)" }}
                >
                  <div
                    className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: 44,
                      height: 44,
                      backgroundColor: "rgba(255,255,255,0.2)",
                    }}
                  >
                    <FontAwesomeIcon icon={icon} />
                  </div>
                  <div>
                    <div className="fw-bold">{t(titleKey)}</div>
                    <div className="small" style={{ opacity: 0.75 }}>
                      {t(descKey)}
                    </div>
                  </div>
                </div>
              ))}
            </Col>
            <Col md={6}>
              <span
                className="badge rounded-pill mb-3 d-inline-block px-3 py-2"
                style={{ backgroundColor: "rgba(255,255,255,0.2)", fontSize: "0.8rem" }}
              >
                {t("user_landing.guest_gallery.badge")}
              </span>
              <h2 className="fw-bold mb-3" style={{ fontSize: "2rem" }}>
                {t("user_landing.guest_gallery.title")}
              </h2>
              <p className="fs-5 mb-4" style={{ opacity: 0.9 }}>
                {t("user_landing.guest_gallery.desc")}
              </p>
              <div className="d-flex flex-wrap gap-2">
                {tags.map((tag, idx) => (
                  <span
                    key={tag}
                    className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                    style={{ backgroundColor: "rgba(255,255,255,0.15)", fontSize: "0.9rem" }}
                  >
                    <FontAwesomeIcon icon={tagIcons[idx]} />
                    {t(`user_landing.guest_gallery.${tag}`)}
                  </span>
                ))}
              </div>
            </Col>
          </Row>
        </SlideIn>
      </Container>
    </section>
  );
}

// ─── Mobile Callout ───────────────────────────────────────────────────────────
function MobileCallout() {
  const { t } = useTranslation();
  return (
    <section className="py-5 text-center" style={{ backgroundColor: "#ffffff" }}>
      <Container>
        <SlideIn>
          <div
            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
            style={{ width: 80, height: 80, backgroundColor: "#faf5ff" }}
          >
            <FontAwesomeIcon icon={faMobileAlt} size="2x" style={{ color: "#6a11cb" }} />
          </div>
          <h2 className="fw-bold mb-3">{t("user_landing.mobile.title")}</h2>
          <p className="text-secondary fs-5 mb-0" style={{ maxWidth: 500, margin: "0 auto" }}>
            {t("user_landing.mobile.desc")}
          </p>
        </SlideIn>
      </Container>
    </section>
  );
}

// ─── Privacy Bar ──────────────────────────────────────────────────────────────
function PrivacyBar() {
  const { t } = useTranslation();
  return (
    <section
      className="py-3"
      style={{ backgroundColor: "#f8f9fa", borderTop: "1px solid #e9ecef" }}
    >
      <Container>
        <div className="d-flex flex-wrap justify-content-center gap-4 text-secondary small">
          {["priv1", "priv2", "priv3"].map((k) => (
            <span key={k} className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faLock} style={{ color: "#6a11cb" }} />
              {t(`user_landing.privacy.${k}`)}
            </span>
          ))}
        </div>
      </Container>
    </section>
  );
}

// ─── CTA ──────────────────────────────────────────────────────────────────────
function CTASection() {
  const { t } = useTranslation();
  return (
    <section
      className="py-5 text-center text-white"
      style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #4a1942 100%)" }}
    >
      <Container>
        <SlideIn>
          <FontAwesomeIcon
            icon={faBolt}
            size="2x"
            className="mb-4"
            style={{ color: "#f857a6" }}
          />
          <h2 className="fw-bold mb-3">{t("user_landing.cta.title")}</h2>
          <p className="fs-5 mb-4" style={{ opacity: 0.75 }}>
            {t("user_landing.cta.desc")}
          </p>
          <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
            <Button
              as={Link}
              to="/login"
              size="lg"
              style={{ backgroundColor: "#f857a6", border: "none", fontWeight: 700 }}
              className="px-5"
            >
              {t("user_landing.cta.btn")}
            </Button>
            <Button
              as={Link}
              to="/"
              size="lg"
              variant="outline-light"
              className="px-5"
            >
              {t("user_landing.cta.secondary")}
            </Button>
          </div>
        </SlideIn>
      </Container>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────
function FAQSection() {
  const { t } = useTranslation();
  const faqs = ["q1", "q2", "q3", "q4"];
  return (
    <section className="py-5" style={{ backgroundColor: "#faf5ff" }}>
      <Container>
        <SlideIn>
          <h2 className="text-center fw-bold mb-5">{t("user_landing.faq.title")}</h2>
          <Row className="justify-content-center">
            <Col md={8}>
              <Accordion flush>
                {faqs.map((q, idx) => (
                  <Accordion.Item
                    eventKey={String(idx)}
                    key={q}
                    className="mb-2 rounded-3 border shadow-sm overflow-hidden"
                  >
                    <Accordion.Header>{t(`user_landing.faq.${q}`)}</Accordion.Header>
                    <Accordion.Body className="text-secondary">
                      {t(`user_landing.faq.a${idx + 1}`)}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>
          </Row>
        </SlideIn>
      </Container>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function PageFooter() {
  const { t } = useTranslation();
  return (
    <footer className="py-4 bg-dark text-white">
      <Container>
        <div className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-2">
          <a
            href="https://www.pic-loud.com"
            className="d-flex align-items-center text-decoration-none"
          >
            <img
              src="/logo_horizontal_white.png"
              alt="Pic-Loud"
              style={{ width: "auto", height: 20 }}
            />
          </a>
          <nav className="d-flex align-items-center gap-3">
            <Link
              to="/privacy"
              className="text-white-50 text-decoration-none small fw-medium"
            >
              {t("user_landing.footer.privacy")}
            </Link>
            <span className="text-white-50 small">|</span>
            <Link
              to="/terms"
              className="text-white-50 text-decoration-none small fw-medium"
            >
              {t("user_landing.footer.terms")}
            </Link>
            <span className="text-white-50 small">|</span>
            <Link
              to="/"
              className="text-white-50 text-decoration-none small fw-medium"
            >
              {t("user_landing.footer.for_photographers")}
            </Link>
          </nav>
          <p className="text-white-50 small mb-0">
            &copy; {new Date().getFullYear()} Pic-Loud. {t("user_landing.footer.rights")}
          </p>
        </div>
      </Container>
    </footer>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
function UserLandingPage() {
  return (
    <div>
      <HeaderLanding headerVariant="" />
      <HeroSection />
      <ProblemSection />
      <FeatureHighlights />
      <HowItWorksSection />
      <EventWallSpotlight />
      <MobileCallout />
      <PrivacyBar />
      <CTASection />
      <FAQSection />
      <PageFooter />
    </div>
  );
}

export default UserLandingPage;
